"use client";

import * as React from "react";
import { Icon } from "@/components/ui/icon";
import { Container } from "@/app/_components/layout/container";
import { featureToolkit } from "@/lib/content/homepage";
import { cn } from "@/lib/utils";

const iconMap = {
  type: "text_fields",
  "zoom-in": "zoom_in",
  palette: "palette",
  focus: "center_focus_strong",
  "link-2": "link",
  contrast: "contrast",
  "image-off": "hide_image",
  highlighter: "ink_highlighter",
  "square-dashed": "crop_free",
  "case-sensitive": "match_case",
  "mouse-pointer-2": "mouse",
  keyboard: "keyboard",
  "volume-x": "volume_off",
  "volume-2": "volume_up",
  sun: "light_mode",
  "circle-pause": "pause_circle",
  "move-horizontal": "swap_horiz",
  "align-justify": "format_align_justify",
  "a-large-small": "format_size",
  "paint-bucket": "format_color_fill",
  paintbrush: "brush",
  "heading-1": "format_h1",
  "mouse-pointer-click": "ads_click",
} as const;

export function FeatureToolkit() {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = React.useState(false);
  const [canNext, setCanNext] = React.useState(true);

  const updateNav = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateNav();
    el.addEventListener("scroll", updateNav, { passive: true });
    window.addEventListener("resize", updateNav);
    return () => {
      el.removeEventListener("scroll", updateNav);
      window.removeEventListener("resize", updateNav);
    };
  }, [updateNav]);

  const scrollByCards = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    const first = el.querySelector<HTMLLIElement>("li");
    const step = first ? first.offsetWidth + 24 : 320;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  return (
    <section
      id="solution"
      aria-label="Toolkit"
      className="bg-clarus-blue text-clarus-dark py-24 md:py-36"
    >
      <Container>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-display-lg text-clarus-dark">
            {featureToolkit.heading}
          </h2>
          <p className="text-subline mt-4 text-clarus-dark/80">
            {featureToolkit.subline}
          </p>
        </div>
      </Container>

      <div className="mt-14 md:mt-20 w-full">
        <div
          ref={scrollRef}
          className="overflow-x-auto scroll-smooth py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          aria-label="Toolkit-Funktionen"
          aria-roledescription="Karussell"
        >
          {/* On wider viewports the carousel extends to the very edges of
              the screen — no blue margin strips on either side. The
              `after:` pseudo acts as a trailing flex item so the last card
              gets breathing room when scrolled to the end (margin-right on
              the last child is clipped by Chrome inside scroll containers;
              the spacer with `-ml-6` cancels the flex `gap` so its width
              equals the actual breathing room). */}
          <ul className="flex snap-x snap-mandatory gap-6 pl-5 pr-2 sm:pl-8 sm:pr-3 lg:pl-12 lg:pr-4 after:content-[''] after:block after:shrink-0 after:-ml-6 after:w-4 sm:after:w-5 lg:after:w-8">
            {featureToolkit.features.map((feature) => {
              const iconName = iconMap[feature.icon as keyof typeof iconMap];
              return (
                <li
                  key={feature.title}
                  /* Width tuned so 4 full cards + ~1/3 peek of the 5th fit
                     at lg+ — same peek proportion as the mobile carousel.
                     Uses 100vw (not the capped 80rem) so cards stretch to
                     fill very wide viewports instead of leaving blue strips
                     on the sides. Hover lift matches the Protection cards. */
                  className="flex shrink-0 snap-start flex-col items-center rounded-card border-[1.5px] border-clarus-dark bg-white px-6 py-8 text-center text-clarus-dark w-[260px] md:w-[300px] lg:w-[calc((100vw-4rem-3.3*1.5rem)/4.3)] transition-[transform,box-shadow] duration-200 ease-[ease] hover:-translate-y-1 hover:shadow-[4px_4px_0_0_var(--color-clarus-dark)] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                >
                  {iconName ? (
                    <Icon
                      name={iconName}
                      className="text-[28px] text-clarus-dark"
                    />
                  ) : null}
                  <h3
                    className="mt-5 hyphens-auto break-words font-extrabold leading-tight text-clarus-dark"
                    lang="de"
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: "1.125rem",
                    }}
                  >
                    {feature.title}
                  </h3>
                  <p className="text-annotation mt-3 text-clarus-dark/75">
                    {feature.body}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <Container className="mt-10">
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => scrollByCards(-1)}
            disabled={!canPrev}
            aria-label="Vorherige Funktion"
            className={cn(
              "flex h-11 w-11 items-center justify-center rounded-full text-clarus-yellow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple focus-visible:ring-offset-2",
              canPrev
                ? "bg-clarus-dark/55 hover:bg-clarus-dark/75"
                : "bg-clarus-dark/35 text-clarus-yellow/80 cursor-not-allowed",
            )}
          >
            <Icon name="arrow_back" className="text-[20px]" />
          </button>
          <button
            type="button"
            onClick={() => scrollByCards(1)}
            disabled={!canNext}
            aria-label="Nächste Funktion"
            className={cn(
              "flex h-11 w-11 items-center justify-center rounded-full text-clarus-yellow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple focus-visible:ring-offset-2",
              canNext
                ? "bg-clarus-dark hover:bg-clarus-dark/85"
                : "bg-clarus-dark/35 text-clarus-yellow/80 cursor-not-allowed",
            )}
          >
            <Icon name="arrow_forward" className="text-[20px]" />
          </button>
        </div>
      </Container>
    </section>
  );
}
